import { useContext, useEffect, useState, useCallback } from "react";
import {
  Icon,
  Dropdown,
  Header,
  Menu,
  Image,
  Loader,
  Button,
  Modal,
  Message,
} from "semantic-ui-react";
import { AuthContext } from "../AuthContext";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  GET_CONNECTED_LINK,
  GET_LL_LINK,
  GOOGLE_CALENDAR_CONNECTION_STATUS,
  GET_GOOGLE_CALENDAR_AUTH_URL,
  DISCONNECT_GOOGLE_CALENDAR,
} from "../graphQueries";

const MenuItems = (props) => {
  const { loginContext, themeColor } = useContext(AuthContext);
  const {
    activeMenu,
    authenticated,
    handleChangeMenu,
    handleGoHome,
    handleLogout,
  } = props;

  const isAuthenticatedUser = loginContext.authenticated;
  const isAdmin = loginContext.role === "Admin";
  const isCSR = loginContext.role === "CSR";
  const isAttorney = loginContext.role === "Attorney";

  // Modal for Google
  const [googleModalOpen, setGoogleModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState(null);

  // Stripe
  const [getConnectedLink] = useLazyQuery(GET_CONNECTED_LINK, {
    fetchPolicy: "network-only",
    onCompleted: (result) => {
      if (result && result.getConnectedLink[0]?.connectedAccountLink) {
        window.open(result.getConnectedLink[0].connectedAccountLink, "_blank");
      }
    },
  });

  // Live Litigation
  const [getLiveLitigationLink] = useLazyQuery(GET_LL_LINK, {
    fetchPolicy: "network-only",
    onCompleted: (result) => {
      if (result && result.getLiveLitigationLink[0]?.liveLink) {
        window.open(result.getLiveLitigationLink[0].liveLink, "_blank");
      }
    },
  });

  // ==== GOOGLE CALENDAR INTEGRATION ====
  const [
    fetchGoogleCalendarStatus,
    {
      data: googleStatusData,
      loading: googleStatusLoading,
      refetch: refetchGoogleStatus,
    },
  ] = useLazyQuery(GOOGLE_CALENDAR_CONNECTION_STATUS, {
    fetchPolicy: "network-only",
  });

  const [getGoogleAuthUrl] = useMutation(GET_GOOGLE_CALENDAR_AUTH_URL);
  const [disconnectGoogleCalendar, { loading: disconnecting }] = useMutation(
    DISCONNECT_GOOGLE_CALENDAR
  );

  const googleConnected = Boolean(
    googleStatusData && googleStatusData.googleCalendarConnectionStatus
  );

  // On mount, check connection status
  useEffect(() => {
    if (isAuthenticatedUser) fetchGoogleCalendarStatus();
    // eslint-disable-next-line
  }, [isAuthenticatedUser]);

  // Connect Google Calendar (opens popup, Express callback does the work)
  const handleConnectGoogle = async () => {
    setModalMessage(null);
    const { data } = await getGoogleAuthUrl();
    const authUrl = data?.getGoogleCalendarAuthUrl;
    if (!authUrl) return;

    const popup = window.open(
      authUrl,
      "Google Calendar Auth",
      "width=600,height=700"
    );

    // If popup blocked, just refetch immediately
    if (!popup) {
      await refetchGoogleStatus();
      return;
    }

    // Poll until popup closes, then refetch status
    const pollTimer = setInterval(async () => {
      if (popup.closed) {
        clearInterval(pollTimer);
        await refetchGoogleStatus();
        setModalMessage({
          type: "success",
          text: "Google Calendar connected!",
        });
        setTimeout(() => setModalMessage(null), 1200);
      }
    }, 400);
  };

  // Disconnect
  const handleDisconnectGoogle = useCallback(async () => {
    setModalMessage(null);
    await disconnectGoogleCalendar();
    await refetchGoogleStatus();
    setModalMessage({
      type: "info",
      text: "Disconnected from Google Calendar.",
    });
    setTimeout(() => setModalMessage(null), 1200);
  }, [disconnectGoogleCalendar, refetchGoogleStatus]);

  // Helper for initials
  const getInitials = (string) => {
    if (!string) return "";
    const desired = string.replace(/[^\w\s]/gi, "");
    const names = desired.split(" ");
    let initials = names[0]?.substring(0, 1).toUpperCase() || "";
    if (names.length > 1) {
      initials += names[names.length - 1]?.substring(0, 1).toUpperCase() || "";
    }
    return initials;
  };

  return (
    <>
      <Menu
        inverted
        size="huge"
        style={{
          height: "50px",
          borderRadius: "0px",
          backgroundColor: themeColor,
          margin: "0px",
          width: "100vw",
        }}
      >
        <Menu.Menu position="left">
          <Menu.Item
            active={activeMenu === "Home"}
            onClick={
              authenticated ? () => handleChangeMenu("Home") : handleGoHome
            }
          >
            <Icon name="calendar alternate" />
            Calendar
          </Menu.Item>
          {isAuthenticatedUser && (
            <Menu.Item
              active={activeMenu === "Jobs"}
              onClick={() => handleChangeMenu("Jobs")}
            >
              <Icon name="list alternate" />
              Jobs
            </Menu.Item>
          )}
          {isAuthenticatedUser && isAdmin && (
            <Menu.Item
              active={activeMenu === "Users"}
              onClick={() => handleChangeMenu("Users")}
            >
              <Icon name="users" />
              Users
            </Menu.Item>
          )}
          {isAuthenticatedUser && isCSR && (
            <Menu.Item
              active={activeMenu === "Availability"}
              onClick={() => handleChangeMenu("Availability")}
            >
              <Icon name="time" />
              Availability
            </Menu.Item>
          )}
          {isAuthenticatedUser && isAttorney && (
            <Menu.Item onClick={() => handleChangeMenu("Jobs", true)}>
              <Icon name="plus" />
              Request New Job
            </Menu.Item>
          )}
          <Menu.Item
            active={activeMenu === "Profile"}
            onClick={() => handleChangeMenu("Profile")}
          >
            <Icon name="user" />
            Profile
          </Menu.Item>
          {isAuthenticatedUser && isCSR && (
            <Menu.Item
              active={activeMenu === "Invite"}
              onClick={() => handleChangeMenu("Invite")}
            >
              <Icon name="send" />
              Invite User
            </Menu.Item>
          )}
        </Menu.Menu>

        <Menu.Menu position="right">
          <Dropdown
            direction="left"
            icon={null}
            trigger={
              <Menu.Item className="custom-button">
                {loginContext.fullName ? (
                  <Header
                    inverted
                    content={getInitials(loginContext.fullName)}
                  />
                ) : (
                  <Icon name="user" />
                )}
              </Menu.Item>
            }
          >
            <Dropdown.Menu>
              <Dropdown.Header>
                {loginContext.fullName}
                <Dropdown.Header
                  style={{ color: "#888888", fontSize: "0.8em" }}
                >
                  ({loginContext.email})
                </Dropdown.Header>
              </Dropdown.Header>
              {(isCSR ||
                loginContext.connectedAccountId ||
                loginContext.liveLitigationLoginId ||
                true) && (
                <>
                  <Dropdown.Divider />
                  <Dropdown.Header>Integrations</Dropdown.Header>
                </>
              )}
              {isCSR && loginContext.connectedAccountId && (
                <Dropdown.Item
                  onClick={() => getConnectedLink()}
                  style={{ display: "flex" }}
                >
                  <Image
                    style={{ width: "20px" }}
                    src="/images/stripe-icon-512.png"
                  />
                  My Stripe Account
                </Dropdown.Item>
              )}
              {isCSR && loginContext.liveLitigationLoginId && (
                <Dropdown.Item
                  onClick={() => getLiveLitigationLink()}
                  style={{ display: "flex" }}
                >
                  <Image style={{ width: "20px" }} src="/images/ll-icon.png" />
                  My LL Account
                </Dropdown.Item>
              )}
              {isCSR && (
                <Dropdown.Item
                  style={{
                    display: "flex",
                    alignItems: "center",
                    minWidth: 200,
                  }}
                  onClick={() => setGoogleModalOpen(true)}
                >
                  <Image
                    style={{ width: "20px", marginRight: 8 }}
                    src="/images/google-icon.png"
                  />
                  Google Calendar
                  {googleStatusLoading && <Loader active inline size="tiny" />}
                </Dropdown.Item>
              )}
              <Dropdown.Divider />
              <Dropdown.Item onClick={handleLogout}>
                <Icon name="logout" />
                Logout
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </Menu.Menu>
      </Menu>

      {/* --- GOOGLE CALENDAR MODAL --- */}
      <Modal
        open={googleModalOpen}
        onClose={() => {
          setGoogleModalOpen(false);
          setModalMessage(null);
        }}
        size="tiny"
      >
        <Modal.Header>
          <Image
            src="/images/google-icon.png"
            avatar
            style={{ marginRight: 10 }}
          />
          Google Calendar Integration
        </Modal.Header>
        <Modal.Content>
          {modalMessage && (
            <Message
              positive={modalMessage.type === "success"}
              negative={modalMessage.type === "error"}
              info={modalMessage.type === "info"}
              content={modalMessage.text}
            />
          )}

          {googleStatusLoading ? (
            <Loader active inline="centered" />
          ) : googleConnected ? (
            <div style={{ textAlign: "center" }}>
              <Icon name="check circle" color="green" size="huge" />
              <p>
                Your Google Calendar is <strong>connected</strong>.
              </p>
              <Button
                color="red"
                basic
                loading={disconnecting}
                onClick={handleDisconnectGoogle}
                style={{ marginTop: 10 }}
                disabled={disconnecting}
              >
                Disconnect
              </Button>
            </div>
          ) : (
            <div style={{ textAlign: "center" }}>
              <Icon name="calendar alternate" color="grey" size="huge" />
              <p>
                Google Calendar is <strong>not connected</strong>.
              </p>
              <Button
                color="blue"
                basic
                onClick={handleConnectGoogle}
                style={{ marginTop: 10 }}
                disabled={googleStatusLoading}
              >
                Connect to Google
              </Button>
            </div>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button
            size="large"
            content="Close"
            onClick={() => setGoogleModalOpen(false)}
          />
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default MenuItems;
